import { useCallback, useEffect, useState } from 'react';
import { ApiCallError } from '@/api/client';
import { snapshotFiles } from '@/lib/dnd';

/**
 * ONE UPLOAD LOOP, FOR EVERY SCREEN THAT ATTACHES A DOCUMENT (#303, finished).
 *
 * ---- ⭐ WHY THIS EXISTS AND `FileDropZone` WAS NOT ENOUGH -------------------
 *
 * `FileDropZone` was created to end four private copies of this logic. Measured
 * 2026-09-08: **it reached exactly one of the four screens.** `PaperworkSliver`
 * and `ScanFolderPanel` still carried their own, and #288 — files lost to an
 * `await` — is documented as having lived in one of the copies that was still
 * there.
 *
 * ⛔⛔ AND THE REASON THE MIGRATION STALLED IS WORTH RECORDING, BECAUSE IT WOULD
 * HAVE STALLED AGAIN. The two hold-outs cannot use the COMPONENT:
 *
 *   - `PaperworkSliver`'s drop target is the WHOLE PANEL, not a box inside it,
 *     and a drop must call `setOpen(true)` — the panels start collapsed (#318),
 *     so without it the result is reported in the one place nobody can see.
 *   - `ScanFolderPanel`'s file input is a FALLBACK inside a bigger control, and
 *     its other upload path (`confirmFiling`) is a different act entirely:
 *     upload AND file, with a `sourceRef` idempotency key.
 *
 * ⭐ **THE SHARED THING IS THE LOOP, NOT THE BOX AROUND IT.** Forcing three
 * screens into one component breaks two of them; extracting the loop gives one
 * implementation and leaves each screen its own presentation.
 *
 * ---- ⛔ THE FIVE THINGS THIS GETS RIGHT SO NOBODY HAS TO -------------------
 *
 * 1. ⛔⛔ **FILES ARE SNAPSHOT BEFORE ANY `await`** (#288).
 *
 *    > Pete, twice: *"trying to attach .pdf's from a local drive is still not
 *    > working and not showing any type of indication failed or success."*
 *
 *    ⚠️ `DataTransfer.files` IS A **LIVE** FileList, and the HTML spec says it
 *    must return the empty list once the drag data store enters protected mode
 *    — which happens the instant the `drop` event finishes dispatching. The
 *    original code called `Array.from(files)` AFTER `await getToken()`, and
 *    `acquireTokenSilent` is genuinely async (MSAL cache, crypto, sometimes an
 *    iframe). By the time the loop ran, **the list was empty**.
 *
 *    ⛔ AND EVERY SYMPTOM FOLLOWS FROM THAT, INCLUDING THE SILENCE:
 *      · `files.length === 0` was checked BEFORE the await, so the guard passed;
 *      · the loop body never ran, so **zero HTTP requests were made**;
 *      · nothing threw, so the `catch` never fired and no error appeared;
 *      · the reload re-fetched unchanged lists, so the screen did not move.
 *    ⭐ **"Uploaded nothing" and "uploaded everything" were indistinguishable.**
 *
 *    ⚠️ IT BROKE THE BROWSE BUTTON THE SAME WAY, for a different reason: a
 *    change handler doing `value = ''` right after the call clears the very same
 *    live list. ⭐ `ScanFolderPanel` had the identical await-then-iterate shape
 *    and never showed it, purely because it cleared its input in a `finally`.
 *    **That difference is the whole story** — and it is why the snapshot lives
 *    here now rather than in each caller's memory.
 * 2. ⛔ **SEQUENTIAL, NOT `Promise.all`.** Ten scans dropped at once would
 *    otherwise open ten concurrent multipart uploads through one browser
 *    connection pool, and on failure leave an unknowable subset stored.
 * 3. ⛔ **IT SAYS SOMETHING ON SUCCESS.** Pete: *"any type of indication failed
 *    or success"*. ⚠️ The COUNT is the point — "2 files added" is a different
 *    fact from "1 file added" when two were dropped.
 * 4. ⛔⛔ **THE COUNT IS REPORTED ON FAILURE TOO.** "3 of 5 uploaded, then this
 *    went wrong" is actionable; "could not upload that file" after three
 *    succeeded is a scheduler re-dropping five and getting eight documents.
 *    ⚠️ THIS IS NEW BEHAVIOUR for `PaperworkSliver` and `ScanFolderPanel` —
 *    neither reported a partial count. It is an improvement, and it is called
 *    out here rather than smuggled in as "just a refactor".
 * 5. ⛔ **`onStart` FIRES BEFORE THE FIRST `await`**, so a caller that must
 *    reveal its own UI (the sliver opening itself) does so while the reader is
 *    still looking at the drop.
 *
 * ---- ⚠️ WHAT IT DELIBERATELY DOES NOT DO ----------------------------------
 *
 * ⚠️ IT DOES NOT KNOW WHERE THE FILE GOES. The caller supplies `upload`, because
 * "into the facility tray, unfiled" and "onto THIS appointment" are different
 * acts and only the caller knows which. A hook that also decided the
 * destination would need a flag per screen, which is the same copies wearing a
 * hat.
 *
 * ⚠️ IT DOES NOT RENDER ANYTHING. `FileDropZone` is still the shared CONTROL for
 * screens that want a box; this is what the box runs on.
 *
 * ⚠️ IT DOES NOT CLEAR THE `<input>`. Every caller now uses the same idiom —
 * **snapshot, clear, run** — which lets the same file be picked twice in a row,
 * a thing people do when they think the first go did not work. ⭐ It is safe
 * ONLY because of rule 1: before the snapshot existed, clearing inline emptied
 * the live FileList the pending call was still holding, and the browse button
 * failed exactly as silently as the drop did. ⛔ `ScanFolderPanel` used to clear
 * in a `finally` and that is why it alone never showed #288; the `finally` went
 * when the snapshot made it redundant, 2026-09-08.
 */

export interface FileUploadRunner {
    /** True from the first file until the last one settles. */
    busy: boolean;
    /**
     * How many files actually reached the server on the last run.
     * ⚠️ Non-null ALONGSIDE `error` when a run partially succeeded — the caller
     * should render both, not treat them as exclusive.
     */
    added: number | null;
    /** A message fit to show a scheduler, or null. */
    error: string | null;
    /** ⛔ Accepts a LIVE FileList. It snapshots internally; do not pre-copy. */
    run: (files: FileList | File[] | null) => Promise<void>;
    /** Clear `added` and `error` — for a caller that dismisses its own banner. */
    reset: () => void;
}

export function useFileUpload(opts: {
    getToken: () => Promise<string>;
    /** ⚠️ Called once PER FILE, sequentially. See rule 2. */
    upload: (token: string, file: File) => Promise<unknown>;
    /** ⭐ Fires before the first await. See rule 5. */
    onStart?: () => void;
    /** Reload whatever list this feeds. Called once, after ALL files. */
    onDone?: () => void | Promise<void>;
    /**
     * ⭐ FOR A CALLER THAT ALREADY HAS ONE ERROR BANNER. `PaperworkSliver` shows
     * `error` in TWO places — one when the panel is collapsed, one inside it when
     * open — and both read the same piece of state. ⛔ Rendering this hook's
     * `error` separately would give that panel a third banner and leave the other
     * two blind to upload failures. So the message is handed back instead.
     * ⚠️ It is called IN ADDITION to setting `error` here, not instead of it.
     */
    onError?: (message: string) => void;
    /** Message when the failure is not an ApiCallError. */
    failureMessage?: string;
    /**
     * ---- ⛔⛔ THE SUCCESS COUNT EXPIRES. THE ERROR DOES NOT. (added 2026-09-10)
     *
     * > Pete: *"1 file added is showing when Attached Paperwork is collapsed"* —
     * > a green row under a folded header, pushing WMS Orders, Labor and
     * > Communications down the rail.
     *
     * ⛔⛔ IT LIVES HERE BECAUSE IT WAS BUILT IN ONE TRAY AND NOT THE OTHER.
     * `PaperworkSliver` grew a private `BANNER_MS` timer; `ScanFolderPanel`'s
     * identical banner had **no expiry at all** and sat there until the next
     * upload. §4.14.3 for the FOURTH time in two days, and the reason this is a
     * hook option rather than a second `useEffect`: a caller cannot forget an
     * argument the way it can forget an effect.
     *
     * ⚠️ ONLY `added` IS CLEARED. `error` stays until the next run or an explicit
     * `reset()` — a failure nobody read is the one thing that must not time out.
     * That asymmetry is the whole point and is not a simplification to make later.
     */
    autoClearMs?: number;
}): FileUploadRunner {
    const { getToken, upload, onStart, onDone, onError, failureMessage, autoClearMs } = opts;

    const [busy, setBusy] = useState(false);
    const [added, setAdded] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const reset = useCallback(() => { setAdded(null); setError(null); }, []);

    /* ⚠️ RESTARTED ON EVERY NEW COUNT, not extended: a second upload inside the
       window must get its own full window rather than inherit what is left of the
       first one's. Cleared on unmount, so a panel switched off mid-window cannot
       set state on a dead component. */
    useEffect(() => {
        if (added === null || !autoClearMs) return;
        const t = window.setTimeout(() => setAdded(null), autoClearMs);
        return () => window.clearTimeout(t);
    }, [added, autoClearMs]);

    const run = useCallback(async (files: FileList | File[] | null) => {
        /* ⛔⛔ RULE 1, AND IT IS THE FIRST STATEMENT ON PURPOSE. Everything below
           this line may await; `files` may be empty by the time it does. */
        const picked = Array.isArray(files) ? files.slice() : snapshotFiles(files);
        if (picked.length === 0) return;

        setBusy(true);
        setAdded(null);
        setError(null);
        onStart?.();

        let done = 0;
        try {
            const token = await getToken();
            for (const f of picked) {
                await upload(token, f);
                done++;
            }
            await onDone?.();
            setAdded(done);
        } catch (err) {
            // ⛔ RULE 4. The count survives the failure.
            if (done > 0) setAdded(done);
            const msg = err instanceof ApiCallError
                ? err.details.error
                : (failureMessage ?? 'Could not upload that file.');
            setError(msg);
            onError?.(msg);
        } finally {
            setBusy(false);
        }
    }, [getToken, upload, onStart, onDone, onError, failureMessage]);

    return { busy, added, error, run, reset };
}
